import {useEffect, useState} from "react"
import {Button, Checkbox, Modal, Tooltip} from "antd"
import {useTranslation} from "react-i18next"
import {useQueryClient} from "@tanstack/react-query"
import {calendarIntegrationService} from "services"
import {useMutation} from "hooks"
import {MY_INTEGRATION_ACCOUNTS_QUERY_KEY} from "hooks/useMyIntegrationAccounts"
import {handleError} from "helpers"
import {IntegrationAccount, IntegrationAccountCalendar} from "types/calendar"
import styles from "./ConnectedCalendars.module.css"

type Props = {
    account: IntegrationAccount
    onClose: () => void
    onSaved: () => void
}

export const ManageCalendarsModal = ({account, onClose, onSaved}: Props) => {
    const {t} = useTranslation(["calendar"])
    const queryClient = useQueryClient()
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

    useEffect(() => {
        setSelectedIds(
            new Set(
                account.calendars.filter((calendar) => calendar.synced).map((calendar) => calendar.providerCalendarId)
            )
        )
    }, [account])

    const syncMutation = useMutation(
        (providerCalendarIds: string[]) =>
            calendarIntegrationService.syncAccountCalendars({accountId: account.id, providerCalendarIds}),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(MY_INTEGRATION_ACCOUNTS_QUERY_KEY)
                onSaved()
                onClose()
            },
            onError: (error) => handleError(error)
        }
    )

    const toggle = (calendar: IntegrationAccountCalendar, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (checked) {
                next.add(calendar.providerCalendarId)
            } else {
                next.delete(calendar.providerCalendarId)
            }
            return next
        })
    }

    const save = () => {
        const providerCalendarIds = [...selectedIds]

        if (providerCalendarIds.length) {
            syncMutation.mutate(providerCalendarIds)
            return
        }

        Modal.confirm({
            title: t("calendar.connectedCalendars.noCalendarsSelectedTitle"),
            content: t("calendar.connectedCalendars.noCalendarsSelectedDescription", {account: account.name}),
            okText: t("calendar.connectedCalendars.disconnectAccount"),
            okButtonProps: {danger: true},
            onOk: () => syncMutation.mutateAsync(providerCalendarIds)
        })
    }

    const getMeta = (calendar: IntegrationAccountCalendar) => {
        if (calendar.isPrimary) {
            return t("calendar.connectedCalendars.primary")
        }
        if (!calendar.canSync) {
            return t("calendar.connectedCalendars.cannotSync")
        }
        if (calendar.owner) {
            return t("calendar.connectedCalendars.sharedBy", {owner: calendar.owner})
        }
        if (!calendar.canEdit) {
            return t("calendar.connectedCalendars.readOnly")
        }
        return null
    }

    return (
        <Modal
            open
            title={t("calendar.connectedCalendars.manageTitle")}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t("calendar.connectedCalendars.cancel")}
                </Button>,
                <Button key="save" type="primary" loading={syncMutation.isLoading} onClick={save}>
                    {t("calendar.connectedCalendars.save")}
                </Button>
            ]}>
            <p className={styles.modalAccount}>{account.name}</p>
            <div className={styles.modalList}>
                {account.calendars.map((calendar) => {
                    const meta = getMeta(calendar)
                    const isChecked = selectedIds.has(calendar.providerCalendarId)
                    // an already synced calendar that can't be synced anymore can still be unselected
                    const isDisabled = !calendar.canSync && !isChecked
                    const row = (
                        <label
                            key={calendar.providerCalendarId}
                            className={styles.modalItem}
                            data-disabled={isDisabled || undefined}>
                            <Checkbox
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={(event) => toggle(calendar, event.target.checked)}
                            />
                            <span className={styles.colorDot} style={{background: calendar.color || undefined}} />
                            <span className={styles.itemContent}>
                                <span className={styles.itemLabel}>{calendar.name}</span>
                                {meta && <span className={styles.itemMeta}>{meta}</span>}
                            </span>
                        </label>
                    )
                    return isDisabled ? (
                        <Tooltip
                            key={calendar.providerCalendarId}
                            title={t("calendar.connectedCalendars.cannotSyncHint")}>
                            {row}
                        </Tooltip>
                    ) : (
                        row
                    )
                })}
            </div>
            <p className={styles.modalHint}>{t("calendar.connectedCalendars.manageHint")}</p>
        </Modal>
    )
}
